import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Transition } from "@headlessui/react";
import Button from "./Button";
import { ReactSVG } from "react-svg";
import CustomInput from "./CustomInput";
import { XMarkIcon } from "@heroicons/react/24/outline";
import ToolTip from "./ToolTip";
import NewToolTip from "./New-ToolTip";
import ClinicalServiceCardToolTip from "./tooltip/clinical-service-card";
import { classNames, scrollY } from "../../helpers/functions";
import cx from 'clsx';

const ConfirmationModalNew = ({
  title,
  content,
  component: ModalComponent,
  type,
  cancelBtnType = "primary-outline",
  confirmText,
  open = false,
  handleClose = () => { },
  confirmAction = () => { },
  cancelAction = null,
  onCross = () => { },
  cancelText = "Cancel",
  iconUrl = "",
  className = "",
  options = [],
  selectedOption = "",
  isNote = false,
  noteTitle = "",
  noteSubTitle = "",
  noteDescription = "",
  position = "bottom",
  verticalButtons = false,
  extraButtonComponent = null,
  belowActionsContent = null,
  disableCancel = false,
  disableConfirm = false,
  requireInput = true,
  optionLabel = "Insurance Plan Name",
  setWideModal = false,
  hideActions = false,
  hideCloseIcon = false,
  hideHR = false,
  titleClassName = "",
  headClassName = "",
  titleIcon = null,
  buttonClassName = "",
  confirmButtonClassName = "",
  actionsClassName = "",
  contentClassName = "pb-8",
  contentWrapperClassName = "",
  belowActionsContentClassName = "px-8 pb-6",
  maskClosable = true
}) => {
  let [iconUrlS, setIconUrlS] = useState(iconUrl);
  const [isClosing, setIsClosing] = useState(false);
  const [disableDelete, setDisableDelete] = useState(false);
  const [updatedOption, setUpdatedOption] = useState("");
  const [reason, setReason] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const ANIMATION_DURATION = 200; // 200ms for smooth UX

  const iconUrlFunction = (_key) => {
    switch (_key) {
      case "danger":
        return setIconUrlS("/icons/information-danger.svg");
        break;
      case "primary":
        return setIconUrlS("");
        break;
      default:
        break;
    }
  };

  const buttonText = (_key) => {
    switch (_key) {
      case "danger":
        return "Delete";
        break;
      case "primary":
        return "Yes, Continue";
        break;
      default:
        break;
    }
  };

  // Smooth close handler with animation
  const handleSmoothClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setUpdatedOption("");
      setReason(null);
      onCross();
    }, ANIMATION_DURATION);
  };

  const handleInputChange = (value) => {
    if (selectedOption !== "" && selectedOption != value)
      setDisableDelete(true);
    else setDisableDelete(false);
    setUpdatedOption(value);
  };

  useEffect(() => {
    // initially set confirm button as disable if we have options to select.
    if (options.length > 0 && requireInput === true) setDisableDelete(true);
  }, [options]);

  useEffect(() => {
    iconUrlFunction(type);
  }, [type]);

  useEffect(() => {
    if (open) {
      setIsClosing(false);
    }
  }, [open]);

  const modalMarkup = (
    <Transition
      enter="transition duration-200 ease-out"
      enterFrom="transform scale-95 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-200 ease-out"
      leaveFrom="transform scale-100 opacity-100"
      leaveTo="transform scale-95 opacity-0"
      className={"fixed z-[50] top-0 left-0 w-full h-screen"}
      show={open && !isClosing}
    >
      <div
        className="fixed inset-0 bg-black/10 z-[1]"
        onClick={() => maskClosable && handleSmoothClose()}
        aria-hidden="true"
      />
      <Transition.Child
        enter="transition duration-200 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-200 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
        className={cx(
          "bg-white z-[2] flex flex-col gap-6 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-[10px] !min-w-[512px] shadow-xl",
          className,
          setWideModal
            ? "max-h-[90vh] w-[800px] pt-6"
            : "w-[512px] max-w-xl max-h-[800px]"
        )}
      >
        {iconUrlS && <ReactSVG src={iconUrlS} className="w-10 h-10 mx-auto" />}
        {title && title.length > 0 && (
          <div>
            <div className={`flex flex-row justify-center gap-4 px-8 pt-8 pb-4 rounded-b-none rounded-[4px] ${headClassName}`}>
              {titleIcon && (
                <div className="flex items-center">
                  {titleIcon}
                </div>
              )}
              <h3
                className={`gap-2 text-teal-300 font-semibold ${titleClassName?.trim() || "text-body-xl"}`}
              >
                {title}
              </h3>
              <div className="absolute flex items-center top-4 right-4">
                {!hideCloseIcon && (
                  <button
                    type="button"
                    className="flex items-center justify-center p-[6px] transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                    onClick={handleSmoothClose}
                  >
                    <span className="sr-only">Close panel</span>
                    <XMarkIcon
                      className="w-5 h-5 m-auto"
                      strokeWidth="2"
                      aria-hidden="true"
                    />
                  </button>
                )}
              </div>
            </div>
            {!hideHR && <hr className="mx-8 border-t border-gray-200" />}
          </div>
        )}
        <div className="flex flex-col flex-1 min-h-0">
          <div className={`px-8 flex-1 ${contentWrapperClassName}`.trim()} id="form-content" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
            <div className={contentClassName}>
              <div
                className={"text-gray-800 text-[16px]"}
                onClick={() => {
                  setTimeout(() => {
                    scrollY("form-content");
                  }, 300);
                }}
              >
                {content()}
              </div>
              {isNote && (
                <p
                  className="relative flex gap-2"
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <ReactSVG
                    src="/icons/information-dark.svg"
                    className="w-6 mt-1"
                    data-tooltip-id={noteTitle}
                    onMouseEnter={() => setShowTooltip(true)}
                    onClick={() => setShowTooltip(true)}
                  />
                  {showTooltip && (
                    <NewToolTip
                      id={noteTitle}
                      place={position}
                      render={() => (
                        <ClinicalServiceCardToolTip
                          noteTitle={noteTitle}
                          noteSubTitle={noteSubTitle}
                          noteDescription={noteDescription}
                        />
                      )}
                    />
                  )}
                </p>
              )}
            </div>
            {ModalComponent && <ModalComponent />}

            {options.length > 0 && (
              <div className="pb-6 relative overflow-visible">
                <CustomInput
                  options={options}
                  name={`label_${0}`}
                  className="col-span-6"
                  label={optionLabel}
                  type="dropdown"
                  required={requireInput}
                  value={updatedOption}
                  error={""}
                  dropdownInFlow={true}
                  customMaxHeight={options.length > 5 ? "max-h-[210px]" : null}
                  onChange={(e) => {
                    handleInputChange(e.value);
                    setReason(null);
                  }}
                />
                {updatedOption === "other" && (
                  <>
                    <CustomInput
                      name="other"
                      className="col-span-12 mt-6"
                      label="Other"
                      placeholder="Enter reason"
                      type="text"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      capitalizeFirstLetter={true}
                    />
                  </>
                )}
              </div>
            )}
          </div>
          {((!hideActions && cancelText != null) || !hideActions || extraButtonComponent) && (
            <div
              className={`px-8 pb-8 grid gap-4  ${!ModalComponent && ""} ${
                !verticalButtons ? "grid-cols-2" : "grid-cols-1"
              } ${actionsClassName}`}
            >
              {!hideActions && cancelText != null && (
                <Button
                  className={`min-w-fit !px-3 ${buttonClassName}`}
                  disabled={disableCancel}
                  type={cancelBtnType}
                  text={cancelText}
                  onClick={() => {
                    setIsClosing(true);
                    setTimeout(() => {
                      setIsClosing(false);
                      if (cancelAction) {
                        cancelAction();
                      } else {
                        handleClose(ConfirmationModalNew.displayName);
                      }
                      setUpdatedOption("");
                      setReason(null);
                    }, ANIMATION_DURATION);
                  }}
                />
              )}
              {!hideActions && (
                <Button
                  className={`min-w-fit !px-3 ${buttonClassName} ${confirmButtonClassName}`}
                  disabled={
                    disableDelete ||
                    disableConfirm ||
                    (options.length > 0 ? updatedOption.length === 0 : false)
                  }
                  type={type}
                  text={confirmText || buttonText(type)}
                  onClick={() => {
                    confirmAction();
                    setUpdatedOption("");
                    setReason(null);
                  }}
                />
              )}

              {extraButtonComponent && extraButtonComponent()}
            </div>
          )}

          {belowActionsContent && (
            <div className={belowActionsContentClassName}>{belowActionsContent()}</div>
          )}
        </div>
      </Transition.Child>
    </Transition>
  );

  // Render modal into document.body via portal so it centers relative to the viewport
  if (typeof document !== 'undefined') {
    return createPortal(modalMarkup, document.body);
  }

  return modalMarkup;
};

export default ConfirmationModalNew;